/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
// Removed Card components to avoid inner border double-render
// import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { getSymbolInfo } from '../api/api';
import TradeControls from './TradeControls';
import RiskManagement from './RiskManagement';
import TradeExecution from './TradeExecution';
import PartialProfitTargets from './PartialProfitTargets';
import TradeCalculations from './TradeCalculations';
import JournalModal from './JournalModal';
import { Accordion, AccordionItem } from './ui/accordion';
import { useAccountData } from '../context/AccountDataProvider';
import { usePriceFeed } from '../context/PriceFeedContext';
import { useTradeState } from '../hooks/useTradeState';
import { useTrade } from '../context/TradeProvider';


function normalizeSymbolInfo(info = {}) {
  const rawConversionRate = info.price_conversion_rate ?? info.priceConversionRate;
  const parsedConversionRate = Number(rawConversionRate);
  return {
    pipSize: info.pip_size ?? info.pipSize ?? null,
    tickSize: info.tick_size ?? info.tickSize ?? null,
    contractSize: info.contract_size ?? info.contractSize ?? null,
    symbolCurrency: info.currency ?? info.symbolCurrency ?? null,
    priceConversionRate: Number.isFinite(parsedConversionRate) ? parsedConversionRate : null,
    lotStep: Number(info.lot_step ?? info.lotStep ?? info.volume_step ?? 0.01) || 0.01,
    minLot:
      typeof info.min_lot === 'number'
        ? info.min_lot
        : (typeof info.minLot === 'number'
            ? info.minLot
            : (typeof info.min_volume === 'number' ? info.min_volume : undefined)),
    maxLot:
      typeof info.max_lot === 'number'
        ? info.max_lot
        : (typeof info.maxLot === 'number'
            ? info.maxLot
            : (typeof info.max_volume === 'number' ? info.max_volume : undefined)),
  };
}

/**
 * Props added (lifted from page):
 * - pipSize, tickSize, contractSize
 * - stopLossPrice, setStopLossPrice
 * - takeProfitPrice, setTakeProfitPrice
 */
const TradePanel = ({
  pipSize: pipSizeProp,
  tickSize: tickSizeProp,
  contractSize: contractSizeProp,
  stopLossPrice,
  setStopLossPrice,
  takeProfitPrice,
  setTakeProfitPrice,
  symbolCurrency: symbolCurrencyProp,
  priceConversionRate: priceConversionRateProp,
  lotStep: lotStepProp,
  minLot: minLotProp,
  maxLot: maxLotProp,
  contentSize = 1.0,
}) => {
  const { accountId, symbol, timeframe } = useTrade();
  const {
    tradeDirection,
    setTradeDirection,
    orderType,
    setOrderType,
    stopLossType,
    setStopLossType,
    riskPercent,
    setRiskPercent,
    atrMultiplier,
    setAtrMultiplier,
    tpMode,
    setTpMode,
    rrRatio,
    setRrRatio,
    takeProfitValue,
    setTakeProfitValue,
  } = useTradeState();

  const {
    balance: accountBalance,
    equity: accountEquity,
    freeMargin,
    cashAvailable,
    currency: accountCurrency,
    connect,
  } = useAccountData();
  const { bid, ask } = usePriceFeed();

  const [symbolInfo, setSymbolInfo] = React.useState(() =>
    normalizeSymbolInfo({
      pipSize: pipSizeProp,
      tickSize: tickSizeProp,
      contractSize: contractSizeProp,
      symbolCurrency: symbolCurrencyProp,
      priceConversionRate: priceConversionRateProp,
      lotStep: lotStepProp,
      minLot: minLotProp,
      maxLot: maxLotProp,
    })
  );

  const [lotSize, setLotSize] = React.useState('N/A');
  const [stopLossDistance, setStopLossDistance] = React.useState('N/A');
  const [finalStopLossDistance, setFinalStopLossDistance] = React.useState(0);
  const [projectedProfit, setProjectedProfit] = React.useState(0);
  const [projectedLoss, setProjectedLoss] = React.useState(0);
  const [reason, setReason] = React.useState('');
  const [limitEntryPrice, setLimitEntryPrice] = React.useState('');
  const [profitMode, setProfitMode] = React.useState('single');
  const [targets, setTargets] = React.useState([
    { rank: 1, share: 0.5, tp_type: 'RR', rr: 1.5, price: '', atr_multiplier: 1 },
    { rank: 2, share: 0.5, tp_type: 'RR', rr: 3.0, price: '', atr_multiplier: 1 },
  ]);
  const [executedTradeId, setExecutedTradeId] = React.useState(null);
  const [showJournalModal, setShowJournalModal] = React.useState(false);

  const effectivePipSize = pipSizeProp ?? symbolInfo.pipSize;
  const effectiveTickSize = tickSizeProp ?? symbolInfo.tickSize;
  const effectiveContractSize = contractSizeProp ?? symbolInfo.contractSize;
  const effectiveSymbolCurrency = symbolCurrencyProp ?? symbolInfo.symbolCurrency;
  const effectivePriceConversionRate =
    priceConversionRateProp ?? symbolInfo.priceConversionRate;
  const effectiveLotStep = Number(lotStepProp ?? symbolInfo.lotStep ?? 0.01) || 0.01;
  const effectiveMinLot = minLotProp ?? symbolInfo.minLot;
  const effectiveMaxLot = maxLotProp ?? symbolInfo.maxLot;

  const resetTradeTerminal = React.useCallback(() => {
    setLotSize('N/A');
    setStopLossPrice && setStopLossPrice('');     // via props (lifted)
    setStopLossDistance('N/A');
    setFinalStopLossDistance(0);
    setProjectedProfit(0);
    setProjectedLoss(0);
    setTakeProfitPrice && setTakeProfitPrice(''); // via props (lifted)
    setLimitEntryPrice('');
    setReason('');
  }, [setStopLossPrice, setTakeProfitPrice]);

  // connect account data stream
  React.useEffect(() => {
    if (accountId) connect(accountId);
    // No disconnect on cleanup — connection persists across page navigation
  }, [accountId, connect]);

  React.useEffect(() => {
    setSymbolInfo((prev) => ({
      ...prev,
      ...normalizeSymbolInfo({
        pipSize: pipSizeProp,
        tickSize: tickSizeProp,
        contractSize: contractSizeProp,
        symbolCurrency: symbolCurrencyProp,
        priceConversionRate: priceConversionRateProp,
        lotStep: lotStepProp,
        minLot: minLotProp,
        maxLot: maxLotProp,
      }),
    }));
  }, [
    pipSizeProp,
    tickSizeProp,
    contractSizeProp,
    symbolCurrencyProp,
    priceConversionRateProp,
    lotStepProp,
    minLotProp,
    maxLotProp,
  ]);

  React.useEffect(() => {
    let cancelled = false;

    async function fetchPanelSymbolInfo() {
      if (!accountId || !symbol) {
        setSymbolInfo(normalizeSymbolInfo({}));
        return;
      }

      try {
        const info = await getSymbolInfo(accountId, symbol);
        if (!cancelled && info) {
          setSymbolInfo(normalizeSymbolInfo(info));
        }
      } catch {
        if (!cancelled) {
          setSymbolInfo(normalizeSymbolInfo({
            pipSize: pipSizeProp,
            tickSize: tickSizeProp,
            contractSize: contractSizeProp,
            symbolCurrency: symbolCurrencyProp,
            priceConversionRate: priceConversionRateProp,
            lotStep: lotStepProp,
            minLot: minLotProp,
            maxLot: maxLotProp,
          }));
        }
      }
    }

    fetchPanelSymbolInfo();
    return () => {
      cancelled = true;
    };
  }, [
    accountId,
    symbol,
    pipSizeProp,
    tickSizeProp,
    contractSizeProp,
    symbolCurrencyProp,
    priceConversionRateProp,
    lotStepProp,
    minLotProp,
    maxLotProp,
  ]);

  // If instrument changes, reset some local calc state
  React.useEffect(() => {
    resetTradeTerminal();
  }, [symbol, timeframe, resetTradeTerminal]);

  React.useEffect(() => {
    setFinalStopLossDistance(stopLossDistance);
  }, [stopLossDistance]);

  const handleTargetsCalculated = (calculatedTargets) => {
    const newTargets = targets.map(originalTarget => {
      const foundCalculated = calculatedTargets.find(ct => ct.rank === originalTarget.rank);
      if (foundCalculated && foundCalculated.calculatedPrice !== undefined) {
        return { ...originalTarget, price: foundCalculated.calculatedPrice };
      }
      return originalTarget;
    });
    setTargets(newTargets);
  };

  // Borderless, natural-height container. Padding kept compact.
  return (
    <div
      className="flex flex-col p-2 space-y-2.5 trade-panel-scale overflow-x-hidden overflow-y-auto"
      style={{ fontSize: `${1 * contentSize}rem` }}
    >
      <div>
        <h2 className="text-xl font-semibold hidden text-textPrimary">Trade Terminal</h2>
        <div className="mt-0.5 space-y-1">
          <p className="flex items-baseline justify-between gap-3">
            <span className="trade-panel-metric-label font-medium text-textSecondary">Balance</span>
            <span className="trade-panel-metric-value font-mono font-semibold text-textPrimary">
              ${typeof accountBalance === 'number' ? accountBalance.toFixed(2) : "Connecting..."}
            </span>
          </p>
          <p className="flex items-baseline justify-between gap-3">
            <span className="trade-panel-metric-label font-medium text-textSecondary">Equity</span>
            <span className="trade-panel-metric-value font-mono font-semibold text-textPrimary">
              ${typeof accountEquity === 'number' ? accountEquity.toFixed(2) : "Connecting..."}
            </span>
          </p>
        </div>
      </div>

      <div>
        <TradeControls
          tradeDirection={tradeDirection}
          setTradeDirection={setTradeDirection}
          selectedAccount={accountId}
          selectedSymbol={symbol}
          orderType={orderType}
          setOrderType={setOrderType}
          limitEntryPrice={limitEntryPrice}
          setLimitEntryPrice={setLimitEntryPrice}
        />
      </div>

      <Accordion>
        <AccordionItem
          title="Symbol Info"
          buttonClassName="trade-panel-accordion-trigger font-medium text-textSecondary hover:bg-transparent"
          titleClassName="trade-panel-field-label"
          contentClassName="trade-panel-accordion-content"
          iconClassName="trade-panel-accordion-icon"
        >
          <div className="space-y-1">
            <div className="trade-panel-meta-text flex flex-wrap gap-x-3 gap-y-1 text-textSecondary">
              <p>
                Pip Size: <span className="font-mono text-textPrimary">{effectivePipSize}</span>
              </p>
              <p>
                Tick Size: <span className="font-mono text-textPrimary">{effectiveTickSize}</span>
              </p>
              <p>
                Contract Size: <span className="font-mono text-textPrimary">{effectiveContractSize}</span>
              </p>
            </div>
          </div>
        </AccordionItem>
      </Accordion>

      <div>
        <h3 className="trade-panel-section-heading mb-1 font-medium text-textSecondary">Risk Management</h3>
        <RiskManagement
          stopLossType={stopLossType}
          setStopLossType={setStopLossType}
          stopLossDistance={stopLossDistance}
          setStopLossDistance={setStopLossDistance}
          stopLossPrice={stopLossPrice}
          setStopLossPrice={setStopLossPrice}
          riskPercent={riskPercent}
          setRiskPercent={setRiskPercent}
          tradeDirection={tradeDirection}
          bidPrice={bid}
          askPrice={ask}
          pipSize={effectivePipSize}
          tickSize={effectiveTickSize}
          contractSize={effectiveContractSize}
          lotStep={effectiveLotStep}
          minLot={effectiveMinLot}
          maxLot={effectiveMaxLot}
          setLotSize={setLotSize}
          accountEquity={accountEquity}
          freeMargin={freeMargin}
          cashAvailable={cashAvailable}
          accountCurrency={accountCurrency}
          symbolCurrency={effectiveSymbolCurrency}
          priceConversionRate={effectivePriceConversionRate}
          takeProfitValue={takeProfitValue}
          setTakeProfitValue={setTakeProfitValue}
          rrRatio={rrRatio}
          setRrRatio={setRrRatio}
          setProjectedProfit={setProjectedProfit}
          projectedProfit={projectedProfit}
          setProjectedLoss={setProjectedLoss}
          projectedLoss={projectedLoss}
          tpMode={tpMode}
          setTpMode={setTpMode}
          takeProfitPrice={takeProfitPrice}
          setTakeProfitPrice={setTakeProfitPrice}
          selectedSymbol={symbol}
          selectedTimeframe={timeframe}
          atrMultiplier={atrMultiplier}
          setAtrMultiplier={setAtrMultiplier}
          profitMode={profitMode}
          setProfitMode={setProfitMode}
          targets={targets}
          onTargetsCalculated={handleTargetsCalculated}
          orderType={orderType}
          limitEntryPrice={limitEntryPrice}
        />
      </div>

      {profitMode === 'multi' && (
        <div>
          <h3 className="trade-panel-section-heading mb-1 font-medium text-textSecondary">Partial Profit Targets</h3>
          <PartialProfitTargets
            accountId={accountId}
            targets={targets}
            setTargets={setTargets}
          />
        </div>
      )}

      <div className="mt-1 border-t trade-panel-muted-border pt-2.5">
        <h3 className="trade-panel-section-heading mb-1 font-medium text-textSecondary">Trade Calculations</h3>
        <TradeCalculations
          lotSize={lotSize}
          projectedLoss={projectedLoss}
          projectedProfit={projectedProfit}
          profitTarget={takeProfitPrice}
          stopLossPrice={stopLossPrice}
          stopLossDistance={stopLossDistance}
        />
      </div>

      <div className="mt-4">
        <TradeExecution
          selectedAccount={accountId}
          selectedSymbol={symbol}
          tradeDirection={tradeDirection}
          lotSize={lotSize}
          bidPrice={bid}
          askPrice={ask}
          stopLossDistance={finalStopLossDistance}
          pipSize={effectivePipSize}
          riskPercent={riskPercent}
          rrRatio={rrRatio}
          projectedProfit={projectedProfit}
          projectedLoss={projectedLoss}
          takeProfitValue={tpMode === "price" ? takeProfitPrice : takeProfitValue}
          tpMode={tpMode}
          takeProfitPrice={takeProfitPrice}
          reason={reason}
          orderType={orderType}
          limitEntryPrice={limitEntryPrice}
          profitMode={profitMode}
          targets={targets}
          lotStep={effectiveLotStep}
          minLot={effectiveMinLot}
          maxLot={effectiveMaxLot}
          setExecutedTradeId={setExecutedTradeId}
          setShowJournalModal={setShowJournalModal}
        />
      </div>

      {showJournalModal && (
        <JournalModal
          tradeId={executedTradeId}
          initialSnapshotSymbol={symbol}
          onClose={() => {
            setShowJournalModal(false);
            setExecutedTradeId(null);
          }}
        />
      )}
    </div>
  );
};

export default TradePanel;
